import { useEffect, useState } from "react";
import styled from "styled-components";
import { QuestionType } from "../../../types/Question";
import {
  AnswerType,
  AnswerValueType,
  PostAnswersBodyType,
} from "../../../types/Answer";
import Question from "../../molecules/Question/Question";
import { postAnswers } from "../../../services/questionnaire.service";
import { setSequenceComponentDone } from "../../../services/sequence.service";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import { useResonanceData } from "../ResonanceContext/ResonanceDataContext";
import useToast from "../../../hooks/useToast";
import {
  formatAnswerForPost,
  hasAsweredFunctionsByType,
} from "../../../helper/answer";
import { calculateQuestionnaireScore } from "../../../helper/questionnaire";
import { QuestionnaireDetailsType } from "../../../types/Questionnaire";
import { Dependency } from "../../../types/Dependency";

interface InlineQuestionProps {
  question: QuestionType; // full question object
  questionnaireId: number; // questionnaire ID
  userSequenceId: number; // user sequence ID
  componentId: number; // sequence component ID
  questionnaireData: QuestionnaireDetailsType; // questionnaire object (for scoring)
  dependencies?: Dependency[]; // dependency array
  isScored?: boolean; // whether the questionnaire is scored
  initialValue?: AnswerValueType | null;
  autoSubmit?: boolean; // submit on answer change
}

const InlineQuestion = ({
  question,
  questionnaireId,
  userSequenceId,
  componentId,
  questionnaireData,
  dependencies = [],
  isScored = true,
  initialValue = null,
  autoSubmit = true,
}: InlineQuestionProps) => {
  const { study } = useConfig();
  const { user } = useAuth();
  const present = useToast();
  const { updateScore, updateSequenceComponentDone, updateSequenceDone } =
    useResonanceData();

  const [value, setValue] = useState<AnswerValueType | null>(
    Array.isArray(initialValue) ? initialValue : (initialValue ?? null),
  );

  useEffect(() => {
    // only set initial value on mount or when it's non-empty and current value is still null/empty
    if (
      initialValue !== null &&
      initialValue !== undefined &&
      (value === null || (Array.isArray(value) && value.length === 0))
    ) {
      setValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once on mount

  const handleChange = (val: AnswerValueType) => {
    setValue(val); // instant UI update
    if (autoSubmit) {
      saveAnswer(val).catch(() => {
        present("Error saving answer");
      });
    }
  };

  const saveAnswer = async (val: AnswerValueType) => {
    try {
      const isEmpty = !hasAsweredFunctionsByType[question.type]?.(val);
      const body = formatAnswerForPost(
        question,
        val,
        Number(question.version ?? 1),
      );

      // 🧩 Step 1: Save the answer
      await postAnswers(
        {
          studyId: study.id,
          userSequenceId: userSequenceId,
          questionnaireId,
          questionId: question.id,
        },
        body,
      );

      // 🧩 Step 2: Calculate total score (single question case)
      const totalScore = calculateQuestionnaireScore(
        questionnaireData.questions,
        [
          {
            questionId: question.id,
            questionType: question.type,
            value: val,
          },
        ],
        questionnaireData.is_scored ?? isScored,
      );

      // 🧩 Step 3: Mark component done (just like SequenceComponent)
      const { data } = await setSequenceComponentDone(
        {
          studyId: study.id,
          userId: user.id,
          sequenceId: userSequenceId.toString(),
          sequenceComponentId: componentId,
        },
        totalScore,
      );

      // 🧩 Step 4: Update local data
      if (questionnaireData.is_scored && totalScore !== null) {
        updateScore(
          data.userSequenceComponentId,
          data.userSequenceId,
          data.sequenceId,
          data.sequenceComponentId,
          data.questionnaireId,
          data.score,
        );
      }

      updateSequenceComponentDone(
        data.userSequenceId,
        data.sequenceComponentId,
      );
      if (data.allDone) updateSequenceDone(data.userSequenceId);
    } catch (err: any) {
      present(err?.response?.data?.message || "Error saving answer");
    }
  };

  return (
    <Wrapper>
      <Question
        question={question}
        answer={value}
        onAnswerChange={handleChange}
      />
      {!autoSubmit && (
        <SubmitButton onClick={() => saveAnswer(value)}>Submit</SubmitButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-width: 380px;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 10px 16px;
  border-radius: 6px;
  background-color: #2db6b4;
  color: white;
  border: none;
  font-weight: 500;
`;

export default InlineQuestion;
